import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { defineMessages, useIntl } from 'react-intl';
import { expandToBackendURL, flattenToAppURL } from '@plone/volto/helpers';
import AppNav from '../AppNav/AppNav';
import {
  formatClientNameMessage,
  formatDueSourceMessage,
  formatMatterReferenceMessage,
  formatTokenMessage,
  sharedMessages,
} from '../../utils/uiMessages';

const initialState = {
  generated_on: '',
  today_count: 0,
  today_hours: 0,
  today_load: 'light',
  upcoming_count: 0,
  upcoming_hours: 0,
  upcoming_load: 'light',
  overdue_count: 0,
  overdue_hours: 0,
  triage_count: 0,
  triage_hours: 0,
  triage_load: 'light',
  blocked_waiting_count: 0,
  blocked_waiting_hours: 0,
  blocked_waiting_load: 'light',
  recommended_today_count: 0,
  recommended_today_hours: 0,
  recommended_today_load: 'light',
  daily_capacity_hours: 3,
  required_today_hours: 0,
  required_today_load: 'light',
  next_7_business_days_required_hours: 0,
  next_7_business_days_average_hours: 0,
  over_capacity_hours: 0,
  later_count: 0,
  today: [],
  upcoming: [],
  overdue: [],
  triage: [],
  blocked_waiting: [],
  recommended_today: [],
  weekly: [],
};

const initialFilters = {
  client: '',
  matter: '',
  status: '',
  priority: '',
};

const DAILY_CAPACITY_STORAGE_KEY = 'pm.daily_capacity_hours';
const DEFAULT_DAILY_CAPACITY_HOURS = 3;

const messages = defineMessages({
  atRisk: {
    id: 'At risk',
    defaultMessage: 'At risk',
  },
  blockedWaiting: {
    id: 'Blocked / Waiting',
    defaultMessage: 'Blocked / Waiting',
  },
  blockedWaitingEmpty: {
    id: 'No blocked or waiting jobs.',
    defaultMessage: 'No blocked or waiting jobs.',
  },
  blockedWaitingNote: {
    id: 'Work that needs an unblock or follow-up. Showing {shown} of {total}.',
    defaultMessage: 'Work that needs an unblock or follow-up. Showing {shown} of {total}.',
  },
  businessDaysLeft: {
    id: '{count} business days left',
    defaultMessage: '{count} business days left',
  },
  capacityForecast: {
    id: 'Capacity forecast',
    defaultMessage: 'Capacity forecast',
  },
  capacityForecastNote: {
    id: 'Distributed from remaining hours across available business days.',
    defaultMessage: 'Distributed from remaining hours across available business days.',
  },
  capacityGap: {
    id: 'Capacity gap',
    defaultMessage: 'Capacity gap',
  },
  capacityOutlook: {
    id: 'Capacity outlook',
    defaultMessage: 'Capacity outlook',
  },
  clearFilters: {
    id: 'Clear filters',
    defaultMessage: 'Clear filters',
  },
  done: {
    id: 'Done',
    defaultMessage: 'Done',
  },
  doneAndFollowUp: {
    id: 'Done + follow-up',
    defaultMessage: 'Done + follow-up',
  },
  dailyCapacity: {
    id: 'Daily capacity',
    defaultMessage: 'Daily capacity',
  },
  dailyCapacityHint: {
    id: 'Use a realistic planning limit for one workday.',
    defaultMessage: 'Use a realistic planning limit for one workday.',
  },
  dueToday: {
    id: 'Due Today',
    defaultMessage: 'Due Today',
  },
  dueTodayEmpty: {
    id: 'No jobs due today.',
    defaultMessage: 'No jobs due today.',
  },
  dueTodayNote: {
    id: '{load} load for today. Showing {shown} of {total}.',
    defaultMessage: '{load} load for today. Showing {shown} of {total}.',
  },
  generatedOn: {
    id: 'Snapshot date: {date}',
    defaultMessage: 'Snapshot date: {date}',
  },
  includeWeekends: {
    id: 'Include weekends in Recommended today',
    defaultMessage: 'Include weekends in Recommended today',
  },
  jobsBeyondWeek4: {
    id: '{count} jobs sitting beyond week 4.',
    defaultMessage: '{count} jobs sitting beyond week 4.',
  },
  loadingWorkload: {
    id: 'Loading workload…',
    defaultMessage: 'Loading workload…',
  },
  needsTriage: {
    id: 'Needs Triage',
    defaultMessage: 'Needs Triage',
  },
  needsTriageEmpty: {
    id: 'No jobs waiting for triage.',
    defaultMessage: 'No jobs waiting for triage.',
  },
  needsTriageNote: {
    id: 'New intake that should be classified before entering the main workload. Showing {shown} of {total}.',
    defaultMessage:
      'New intake that should be classified before entering the main workload. Showing {shown} of {total}.',
  },
  next7BusinessDays: {
    id: 'Next 7 Business Days',
    defaultMessage: 'Next 7 Business Days',
  },
  next7BusinessDaysAverage: {
    id: 'Next 7-day pace',
    defaultMessage: 'Next 7-day pace',
  },
  next7BusinessDaysAverageMeta: {
    id: '{hours}h total required over the next 7 business days',
    defaultMessage: '{hours}h total required over the next 7 business days',
  },
  next7BusinessDaysEmpty: {
    id: 'No jobs due in the next 7 business days.',
    defaultMessage: 'No jobs due in the next 7 business days.',
  },
  next7BusinessDaysNote: {
    id: '{load} near-term load. Showing {shown} of {total}.',
    defaultMessage: '{load} near-term load. Showing {shown} of {total}.',
  },
  openJobs: {
    id: '{count} open jobs',
    defaultMessage: '{count} open jobs',
  },
  openTools: {
    id: 'Open tools',
    defaultMessage: 'Open tools',
  },
  overdue: {
    id: 'Overdue',
    defaultMessage: 'Overdue',
  },
  overCapacityMeta: {
    id: '{hours}h above daily capacity',
    defaultMessage: '{hours}h above daily capacity',
  },
  overdueEmpty: {
    id: 'No overdue jobs.',
    defaultMessage: 'No overdue jobs.',
  },
  overdueNote: {
    id: 'Clear these first before new docket work. Showing {shown} of {total}.',
    defaultMessage: 'Clear these first before new docket work. Showing {shown} of {total}.',
  },
  personalPlanning: {
    id: 'Personal planning',
    defaultMessage: 'Personal planning',
  },
  requiredDailyHours: {
    id: 'Need {hours}h/day',
    defaultMessage: 'Need {hours}h/day',
  },
  requiredToday: {
    id: 'Required today',
    defaultMessage: 'Required today',
  },
  requiredTodayMeta: {
    id: 'To stay on pace for currently actionable work',
    defaultMessage: 'To stay on pace for currently actionable work',
  },
  recommendedPullForwardEmpty: {
    id: 'No recommended pull-forward work today.',
    defaultMessage: 'No recommended pull-forward work today.',
  },
  recommendedToday: {
    id: 'Recommended Today',
    defaultMessage: 'Recommended Today',
  },
  recommendedTodayNote: {
    id: 'Work whose calculated start-by date has arrived. Showing {shown} of {total}.',
    defaultMessage:
      'Work whose calculated start-by date has arrived. Showing {shown} of {total}.',
  },
  rollingWeeklyLoad: {
    id: 'Rolling weekly load after the next 7 business days.',
    defaultMessage: 'Rolling weekly load after the next 7 business days.',
  },
  visibleJobs: {
    id: 'Visible jobs',
    defaultMessage: 'Visible jobs',
  },
  visibleJobsCount: {
    id: '{count} visible jobs',
    defaultMessage: '{count} visible jobs',
  },
  visibleWorkFilters: {
    id: 'Visible work filters',
    defaultMessage: 'Visible work filters',
  },
  visibleWorkFiltersNote: {
    id: 'These filters affect triage, execution, and near-term planning lists.',
    defaultMessage: 'These filters affect triage, execution, and near-term planning lists.',
  },
  withinCapacity: {
    id: 'Within daily capacity',
    defaultMessage: 'Within daily capacity',
  },
});

function parseBooleanParam(value) {
  return ['1', 'true', 'yes', 'on'].includes(String(value || '').toLowerCase());
}

function toneClass(prefix, value) {
  return `${prefix} ${prefix}--${(value || 'neutral').replace(/_/g, '-')}`;
}

function SummaryStat({ label, value, meta, tone = 'neutral' }) {
  return (
    <div className={toneClass('patent-dashboard__stat', tone)}>
      <span className="patent-dashboard__stat-label">{label}</span>
      <strong className="patent-dashboard__stat-value">{value}</strong>
      {meta ? <span className="patent-dashboard__stat-meta">{meta}</span> : null}
    </div>
  );
}

function SelectField({ label, name, value, options, onChange }) {
  const intl = useIntl();

  return (
    <label className="patent-dashboard__filter-field">
      <span className="patent-dashboard__filter-label">{label}</span>
      <select
        className="patent-dashboard__filter-select"
        name={name}
        value={value}
        onChange={onChange}
      >
        <option value="">{intl.formatMessage(sharedMessages.all)}</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {formatTokenMessage(intl, option)}
          </option>
        ))}
      </select>
    </label>
  );
}

async function extractError(response) {
  try {
    const payload = await response.json();
    return (
      payload.error ||
      payload.message ||
      payload.error?.message ||
      `Request failed with status ${response.status}`
    );
  } catch {
    return `Request failed with status ${response.status}`;
  }
}

function JobList({ items, emptyText, onAction, busyKey }) {
  const intl = useIntl();

  if (!items.length) {
    return <p className="patent-dashboard__empty">{emptyText}</p>;
  }

  return (
    <ul className="patent-dashboard__jobs">
      {items.map((job) => {
        const jobKey = normalizeContentPath(job.url);
        const busy = busyKey === jobKey;
        return (
        <li key={`${job.url}-${job.title}`} className="patent-dashboard__job">
          <div className="patent-dashboard__job-row">
            <Link
              to={jobEditorUrl(job.url)}
              className="patent-dashboard__job-title"
            >
              {job.title}
            </Link>
            <span className="patent-dashboard__hours">{displayJobHours(job)}h</span>
          </div>
          <div className="patent-dashboard__job-row patent-dashboard__job-row--context">
            <span className="patent-dashboard__context-line">
              {intl.formatMessage(sharedMessages.matterRef)}:{' '}
              {formatMatterReferenceMessage(intl, job.matter_reference_code)}
            </span>
          </div>
          <div className="patent-dashboard__job-row patent-dashboard__job-row--meta">
            <span className="patent-dashboard__meta">
              {formatClientNameMessage(intl, job.client_name)}
            </span>
          </div>
          <div className="patent-dashboard__chips">
            <span className={toneClass('patent-dashboard__chip', job.priority)}>
              {formatTokenMessage(intl, job.priority)}
            </span>
            <span className={toneClass('patent-dashboard__chip', job.status)}>
              {formatTokenMessage(intl, job.status)}
            </span>
            {job.at_risk ? (
              <span className="patent-dashboard__chip patent-dashboard__chip--risk">
                {intl.formatMessage(messages.atRisk)}
              </span>
            ) : null}
            <span className="patent-dashboard__chip patent-dashboard__chip--kind">
              {formatTokenMessage(intl, job.job_kind)}
            </span>
            <span className="patent-dashboard__chip patent-dashboard__chip--due">
              {intl.formatMessage(sharedMessages.internalDue)} {job.internal_due_date}
            </span>
            <span className="patent-dashboard__chip patent-dashboard__chip--source">
              {formatDueSourceMessage(intl, job.due_source)}
            </span>
            {job.recommended_start_on ? (
              <span className="patent-dashboard__chip patent-dashboard__chip--start">
                {intl.formatMessage({ id: 'Start by', defaultMessage: 'Start by' })}{' '}
                {job.recommended_start_on}
              </span>
            ) : null}
            {job.business_days_remaining > 0 ? (
              <span className="patent-dashboard__chip patent-dashboard__chip--pace">
                {intl.formatMessage(messages.businessDaysLeft, {
                  count: job.business_days_remaining,
                })}
              </span>
            ) : null}
            {job.business_days_remaining > 0 && job.required_daily_hours > 0 ? (
              <span className="patent-dashboard__chip patent-dashboard__chip--pace">
                {intl.formatMessage(messages.requiredDailyHours, {
                  hours: formatHours(job.required_daily_hours),
                })}
              </span>
            ) : null}
            {job.legal_due_date ? (
              <span className="patent-dashboard__chip patent-dashboard__chip--deadline">
                {intl.formatMessage({ id: 'Legal', defaultMessage: 'Legal' })}{' '}
                {job.legal_due_date}
              </span>
            ) : null}
            {job.client_due_date ? (
              <span className="patent-dashboard__chip patent-dashboard__chip--deadline">
                {intl.formatMessage({ id: 'Client', defaultMessage: 'Client' })}{' '}
                {job.client_due_date}
              </span>
            ) : null}
          </div>
          <div className="patent-dashboard__job-actions">
            <button
              className="patent-entry__ghost patent-dashboard__job-action patent-dashboard__job-action--done"
              type="button"
              disabled={busy}
              onClick={() => onAction(job, 'mark_done')}
            >
              {busy
                ? intl.formatMessage(sharedMessages.updating)
                : intl.formatMessage(messages.done)}
            </button>
            <button
              className="patent-entry__ghost patent-dashboard__job-action patent-dashboard__job-action--follow-up"
              type="button"
              disabled={busy}
              onClick={() =>
                onAction(job, 'mark_done', {
                  redirectTo: followUpRegistrationUrl(job),
                })
              }
            >
              {busy
                ? intl.formatMessage(sharedMessages.updating)
                : intl.formatMessage(messages.doneAndFollowUp)}
            </button>
          </div>
        </li>
      )})}
    </ul>
  );
}

function displayJobHours(job) {
  return job?.planning_hours ?? job?.estimated_hours ?? 0;
}

function formatHours(value) {
  const normalized = Number(value || 0);
  if (!Number.isFinite(normalized)) {
    return '0';
  }
  return Number.isInteger(normalized) ? String(normalized) : normalized.toFixed(1);
}

function readDailyCapacityHours() {
  if (typeof window === 'undefined') {
    return DEFAULT_DAILY_CAPACITY_HOURS;
  }
  const stored = window.localStorage.getItem(DAILY_CAPACITY_STORAGE_KEY);
  const parsed = Number.parseFloat(stored || '');
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return DEFAULT_DAILY_CAPACITY_HOURS;
  }
  return parsed;
}

function jobEditorUrl(url) {
  return `/job-editor?path=${encodeURIComponent(normalizeContentPath(url))}`;
}

function followUpRegistrationUrl(job) {
  const jobPath = normalizeContentPath(job?.url || '');
  return `/register-job?source_job_path=${encodeURIComponent(jobPath)}`;
}

function normalizeContentPath(url) {
  const appUrl = flattenToAppURL(url || '');
  const origin =
    typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  try {
    return new URL(appUrl, origin).pathname;
  } catch {
    return appUrl;
  }
}

function WeekCard({ week }) {
  const intl = useIntl();

  return (
    <article className={toneClass('patent-dashboard__week', week.load)}>
      <div className="patent-dashboard__week-head">
        <h3>{week.label}</h3>
        <span className="patent-dashboard__week-load">
          {formatTokenMessage(intl, week.load)}
        </span>
      </div>
      <p className="patent-dashboard__week-hours">{week.hours}h</p>
      <p className="patent-dashboard__week-jobs">
        {intl.formatMessage(messages.openJobs, { count: week.job_count })}
      </p>
    </article>
  );
}

export default function OverviewPage(props) {
  const intl = useIntl();
  const token = useSelector((state) => state.userSession?.token);
  const search = props?.location?.search || '';
  const initialIncludeWeekends = parseBooleanParam(
    new URLSearchParams(search).get('include_weekends'),
  );
  const [data, setData] = useState(initialState);
  const [filters, setFilters] = useState(initialFilters);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [actionError, setActionError] = useState('');
  const [actingOn, setActingOn] = useState('');
  const [includeWeekends, setIncludeWeekends] = useState(initialIncludeWeekends);
  const [dailyCapacityHours, setDailyCapacityHours] = useState(readDailyCapacityHours);

  useEffect(() => {
    document.body.classList.add('patent-dashboard-page');
    return () => {
      document.body.classList.remove('patent-dashboard-page');
    };
  }, [includeWeekends]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    window.localStorage.setItem(
      DAILY_CAPACITY_STORAGE_KEY,
      String(dailyCapacityHours),
    );
    return undefined;
  }, [dailyCapacityHours]);

  useEffect(() => {
    let active = true;

    async function loadSummary() {
      try {
        const params = new URLSearchParams();
        if (includeWeekends) {
          params.set('include_weekends', '1');
        }
        params.set('daily_capacity_hours', String(dailyCapacityHours));
        const query = params.toString() ? `?${params.toString()}` : '';
        const response = await fetch(
          expandToBackendURL(`/@patent-workload-summary${query}`),
          {
            headers: {
              Accept: 'application/json',
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          },
        );
        if (!response.ok) {
          throw new Error(await extractError(response));
        }
        const payload = await response.json();
        if (active) {
          setData({ ...initialState, ...payload });
        }
      } catch (loadError) {
        if (active) {
          setError(loadError.message);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadSummary();
    return () => {
      active = false;
    };
  }, [dailyCapacityHours, includeWeekends, token]);

  async function refreshSummary() {
    const params = new URLSearchParams();
    if (includeWeekends) {
      params.set('include_weekends', '1');
    }
    params.set('daily_capacity_hours', String(dailyCapacityHours));
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await fetch(
      expandToBackendURL(`/@patent-workload-summary${query}`),
      {
        headers: {
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      },
    );
    if (!response.ok) {
      throw new Error(await extractError(response));
    }
    const payload = await response.json();
    setData({ ...initialState, ...payload });
  }

  async function onAction(job, action, options = {}) {
    const jobPath = normalizeContentPath(job.url);
    setActingOn(jobPath);
    setActionError('');
    try {
      const response = await fetch(expandToBackendURL('/@job-actions'), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          job_path: jobPath,
          action,
        }),
      });
      if (!response.ok) {
        throw new Error(await extractError(response));
      }
      if (options.redirectTo) {
        window.location.assign(options.redirectTo);
        return;
      }
      await refreshSummary();
    } catch (actionError) {
      setActionError(actionError.message);
    } finally {
      setActingOn('');
    }
  }

  useEffect(() => {
    const nextSearch = includeWeekends ? '?include_weekends=1' : '';
    if (window.location.search !== nextSearch) {
      window.history.replaceState({}, '', `${window.location.pathname}${nextSearch}`);
    }
  }, [includeWeekends]);

  if (loading) {
    return (
      <div className="patent-dashboard patent-dashboard--loading">
        {intl.formatMessage(messages.loadingWorkload)}
      </div>
    );
  }

  if (error) {
    return (
      <div className="patent-dashboard patent-dashboard--error">
        {error}
      </div>
    );
  }

  const filterableJobs = dedupeJobs([
    ...data.triage,
    ...data.today,
    ...data.upcoming,
    ...data.overdue,
    ...data.blocked_waiting,
    ...data.recommended_today,
  ]);
  const filterOptions = {
    client: uniqueValues(filterableJobs, 'client_name'),
    matter: uniqueValues(filterableJobs, 'matter_reference_code'),
    status: uniqueValues(filterableJobs, 'status'),
    priority: uniqueValues(filterableJobs, 'priority'),
  };
  const filteredToday = data.today.filter((job) => matchesFilters(job, filters));
  const filteredTriage = data.triage.filter((job) => matchesFilters(job, filters));
  const filteredUpcoming = data.upcoming.filter((job) =>
    matchesFilters(job, filters),
  );
  const filteredOverdue = data.overdue.filter((job) => matchesFilters(job, filters));
  const filteredBlockedWaiting = data.blocked_waiting.filter((job) =>
    matchesFilters(job, filters),
  );
  const filteredRecommendedToday = data.recommended_today.filter((job) =>
    matchesFilters(job, filters),
  );
  const capacityGapTone = data.over_capacity_hours > 0 ? 'critical' : 'light';
  const next7AverageTone =
    data.next_7_business_days_average_hours > data.daily_capacity_hours
      ? 'high'
      : 'light';
  const filteredCount = dedupeJobs([
    ...filteredTriage,
    ...filteredToday,
    ...filteredUpcoming,
    ...filteredOverdue,
    ...filteredBlockedWaiting,
    ...filteredRecommendedToday,
  ]).length;

  return (
    <main className="patent-dashboard">
      <header className="patent-dashboard__hero">
        <div>
          <AppNav currentPath="/overview" />
          <p className="patent-dashboard__eyebrow">
            {intl.formatMessage(sharedMessages.patentManager)}
          </p>
          <h1>{intl.formatMessage(sharedMessages.overview)}</h1>
          <p className="patent-dashboard__snapshot">
            {intl.formatMessage(messages.generatedOn, { date: data.generated_on })}
          </p>
          <div className="patent-dashboard__quick-actions">
            <Link className="patent-dashboard__action" to="/register-job">
              {intl.formatMessage(sharedMessages.newJob)}
            </Link>
          </div>
        </div>
        <div className="patent-dashboard__stats">
          <SummaryStat
            label={intl.formatMessage(messages.needsTriage)}
            value={`${data.triage_hours}h`}
            meta={intl.formatMessage(messages.openJobs, { count: data.triage_count })}
            tone={data.triage_count ? 'high' : 'light'}
          />
          <SummaryStat
            label={intl.formatMessage(messages.overdue)}
            value={`${data.overdue_hours}h`}
            meta={intl.formatMessage(messages.openJobs, { count: data.overdue_count })}
            tone={data.overdue_hours ? 'critical' : 'light'}
          />
          <SummaryStat
            label={intl.formatMessage(messages.dueToday)}
            value={`${data.today_hours}h`}
            meta={intl.formatMessage(messages.openJobs, { count: data.today_count })}
            tone={data.today_load}
          />
          <SummaryStat
            label={intl.formatMessage(messages.recommendedToday)}
            value={`${data.recommended_today_hours}h`}
            meta={intl.formatMessage(messages.openJobs, { count: data.recommended_today_count })}
            tone={data.recommended_today_load}
          />
          <SummaryStat
            label={intl.formatMessage(messages.next7BusinessDays)}
            value={`${data.upcoming_hours}h`}
            meta={intl.formatMessage(messages.openJobs, { count: data.upcoming_count })}
            tone={data.upcoming_load}
          />
          <SummaryStat
            label={intl.formatMessage(messages.blockedWaiting)}
            value={`${data.blocked_waiting_hours}h`}
            meta={intl.formatMessage(messages.openJobs, { count: data.blocked_waiting_count })}
            tone={data.blocked_waiting_count ? 'high' : 'light'}
          />
        </div>
      </header>

      {actionError ? (
        <p className="patent-entry__error">{actionError}</p>
      ) : null}

      <section className="patent-dashboard__filters">
          <div className="patent-dashboard__filters-head">
            <div>
              <h2>{intl.formatMessage(messages.visibleWorkFilters)}</h2>
              <p className="patent-dashboard__section-note">
                {intl.formatMessage(messages.visibleWorkFiltersNote)}
              </p>
            </div>
          <div className="patent-dashboard__filters-actions">
            <span className="patent-dashboard__filter-count">
              {intl.formatMessage(messages.visibleJobsCount, { count: filteredCount })}
            </span>
            <button
              className="patent-dashboard__filter-reset"
              type="button"
              onClick={() => setFilters(initialFilters)}
            >
              {intl.formatMessage(messages.clearFilters)}
            </button>
          </div>
        </div>
        <div className="patent-dashboard__filters-grid">
          <SelectField
            label={intl.formatMessage(sharedMessages.client)}
            name="client"
            value={filters.client}
            options={filterOptions.client}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                client: event.target.value,
              }))
            }
          />
          <SelectField
            label={intl.formatMessage(sharedMessages.matter)}
            name="matter"
            value={filters.matter}
            options={filterOptions.matter}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                matter: event.target.value,
              }))
            }
          />
          <SelectField
            label={intl.formatMessage(sharedMessages.status)}
            name="status"
            value={filters.status}
            options={filterOptions.status}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                status: event.target.value,
              }))
            }
          />
          <SelectField
            label={intl.formatMessage(sharedMessages.priority)}
            name="priority"
            value={filters.priority}
            options={filterOptions.priority}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                priority: event.target.value,
              }))
            }
          />
          <label className="patent-dashboard__filter-field patent-dashboard__filter-field--checkbox">
            <span className="patent-dashboard__filter-label">
              {intl.formatMessage(messages.personalPlanning)}
            </span>
            <label className="patent-dashboard__toggle">
              <input
                type="checkbox"
                checked={includeWeekends}
                onChange={(event) => setIncludeWeekends(event.target.checked)}
              />
              <span>{intl.formatMessage(messages.includeWeekends)}</span>
            </label>
          </label>
          <label className="patent-dashboard__filter-field">
            <span className="patent-dashboard__filter-label">
              {intl.formatMessage(messages.dailyCapacity)}
            </span>
            <input
              className="patent-entry__input"
              type="number"
              min="0.5"
              step="0.5"
              value={dailyCapacityHours}
              onChange={(event) => {
                const nextValue = Number.parseFloat(event.target.value);
                if (!Number.isFinite(nextValue) || nextValue <= 0) {
                  setDailyCapacityHours(DEFAULT_DAILY_CAPACITY_HOURS);
                  return;
                }
                setDailyCapacityHours(nextValue);
              }}
            />
          </label>
        </div>
        <p className="patent-dashboard__section-note">
          {intl.formatMessage(messages.dailyCapacityHint)}
        </p>
      </section>

      <section className="patent-dashboard__card patent-dashboard__card--wide">
        <div className="patent-dashboard__section-header">
          <div>
            <h2>{intl.formatMessage(messages.capacityForecast)}</h2>
            <p className="patent-dashboard__section-note">
              {intl.formatMessage(messages.capacityForecastNote)}
            </p>
          </div>
        </div>
        <div className="patent-dashboard__stats">
          <SummaryStat
            label={intl.formatMessage(messages.dailyCapacity)}
            value={`${formatHours(data.daily_capacity_hours)}h/day`}
            meta={intl.formatMessage(messages.dailyCapacityHint)}
            tone="light"
          />
          <SummaryStat
            label={intl.formatMessage(messages.requiredToday)}
            value={`${formatHours(data.required_today_hours)}h/day`}
            meta={intl.formatMessage(messages.requiredTodayMeta)}
            tone={data.required_today_load}
          />
          <SummaryStat
            label={intl.formatMessage(messages.next7BusinessDaysAverage)}
            value={`${formatHours(data.next_7_business_days_average_hours)}h/day`}
            meta={intl.formatMessage(messages.next7BusinessDaysAverageMeta, {
              hours: formatHours(data.next_7_business_days_required_hours),
            })}
            tone={next7AverageTone}
          />
          <SummaryStat
            label={intl.formatMessage(messages.capacityGap)}
            value={`${formatHours(data.over_capacity_hours)}h`}
            meta={
              data.over_capacity_hours > 0
                ? intl.formatMessage(messages.overCapacityMeta, {
                    hours: formatHours(data.over_capacity_hours),
                  })
                : intl.formatMessage(messages.withinCapacity)
            }
            tone={capacityGapTone}
          />
        </div>
      </section>

      <section className="patent-dashboard__grid">
        <article className="patent-dashboard__card">
          <div className="patent-dashboard__section-header">
            <div>
              <h2>{intl.formatMessage(messages.needsTriage)}</h2>
              <p className="patent-dashboard__section-note">
                {intl.formatMessage(messages.needsTriageNote, {
                  shown: filteredTriage.length,
                  total: data.triage.length,
                })}
              </p>
            </div>
          </div>
          <JobList
            items={filteredTriage}
            emptyText={intl.formatMessage(messages.needsTriageEmpty)}
            onAction={onAction}
            busyKey={actingOn}
          />
        </article>

        <article className="patent-dashboard__card">
          <div className="patent-dashboard__section-header">
            <div>
              <h2>{intl.formatMessage(messages.overdue)}</h2>
              <p className="patent-dashboard__section-note">
                {intl.formatMessage(messages.overdueNote, {
                  shown: filteredOverdue.length,
                  total: data.overdue.length,
                })}
              </p>
            </div>
          </div>
          <JobList
            items={filteredOverdue}
            emptyText={intl.formatMessage(messages.overdueEmpty)}
            onAction={onAction}
            busyKey={actingOn}
          />
        </article>

        <article className="patent-dashboard__card">
          <div className="patent-dashboard__section-header">
            <div>
              <h2>{intl.formatMessage(messages.dueToday)}</h2>
              <p className="patent-dashboard__section-note">
                {intl.formatMessage(messages.dueTodayNote, {
                  load: formatTokenMessage(intl, data.today_load),
                  shown: filteredToday.length,
                  total: data.today.length,
                })}
              </p>
            </div>
          </div>
          <JobList
            items={filteredToday}
            emptyText={intl.formatMessage(messages.dueTodayEmpty)}
            onAction={onAction}
            busyKey={actingOn}
          />
        </article>

        <article className="patent-dashboard__card">
          <div className="patent-dashboard__section-header">
            <div>
              <h2>{intl.formatMessage(messages.recommendedToday)}</h2>
              <p className="patent-dashboard__section-note">
                {intl.formatMessage(messages.recommendedTodayNote, {
                  shown: filteredRecommendedToday.length,
                  total: data.recommended_today.length,
                })}
              </p>
            </div>
          </div>
          <JobList
            items={filteredRecommendedToday}
            emptyText={intl.formatMessage(messages.recommendedPullForwardEmpty)}
            onAction={onAction}
            busyKey={actingOn}
          />
        </article>

        <article className="patent-dashboard__card">
          <div className="patent-dashboard__section-header">
            <div>
              <h2>{intl.formatMessage(messages.next7BusinessDays)}</h2>
              <p className="patent-dashboard__section-note">
                {intl.formatMessage(messages.next7BusinessDaysNote, {
                  load: formatTokenMessage(intl, data.upcoming_load),
                  shown: filteredUpcoming.length,
                  total: data.upcoming.length,
                })}
              </p>
            </div>
          </div>
          <JobList
            items={filteredUpcoming}
            emptyText={intl.formatMessage(messages.next7BusinessDaysEmpty)}
            onAction={onAction}
            busyKey={actingOn}
          />
        </article>

        <article className="patent-dashboard__card">
          <div className="patent-dashboard__section-header">
            <div>
              <h2>{intl.formatMessage(messages.blockedWaiting)}</h2>
              <p className="patent-dashboard__section-note">
                {intl.formatMessage(messages.blockedWaitingNote, {
                  shown: filteredBlockedWaiting.length,
                  total: data.blocked_waiting.length,
                })}
              </p>
            </div>
          </div>
          <JobList
            items={filteredBlockedWaiting}
            emptyText={intl.formatMessage(messages.blockedWaitingEmpty)}
            onAction={onAction}
            busyKey={actingOn}
          />
        </article>
      </section>

      <section className="patent-dashboard__card patent-dashboard__card--wide">
        <div className="patent-dashboard__section-header">
          <div>
            <h2>{intl.formatMessage(messages.capacityOutlook)}</h2>
            <p className="patent-dashboard__section-note">
              {intl.formatMessage(messages.rollingWeeklyLoad)}
            </p>
          </div>
          <p className="patent-dashboard__section-note">
            {intl.formatMessage(messages.jobsBeyondWeek4, { count: data.later_count })}
          </p>
        </div>
        <div className="patent-dashboard__weeks">
          {data.weekly.map((week) => (
            <WeekCard key={week.label} week={week} />
          ))}
        </div>
      </section>

      <section className="patent-dashboard__utility">
        <div className="patent-dashboard__utility-item">
          <span className="patent-dashboard__utility-label">
            {intl.formatMessage({ id: 'Snapshot', defaultMessage: 'Snapshot' })}
          </span>
          <strong>{data.generated_on}</strong>
        </div>
        <div className="patent-dashboard__utility-item">
          <span className="patent-dashboard__utility-label">
            {intl.formatMessage(messages.visibleJobs)}
          </span>
          <strong>{filteredCount}</strong>
        </div>
        <div className="patent-dashboard__utility-links">
          <Link className="patent-dashboard__utility-link" to="/register-matter">
            {intl.formatMessage(sharedMessages.registerMatter)}
          </Link>
          <Link className="patent-dashboard__utility-link" to="/register-job">
            {intl.formatMessage(sharedMessages.registerJob)}
          </Link>
          <Link className="patent-dashboard__utility-link" to="/tools">
            {intl.formatMessage(messages.openTools)}
          </Link>
        </div>
      </section>
    </main>
  );
}

function uniqueValues(items, field) {
  return Array.from(
    new Set(
      items
        .map((item) => item[field])
        .filter(Boolean),
    ),
  ).sort((left, right) => left.localeCompare(right));
}

function dedupeJobs(items) {
  const seen = new Set();
  return items.filter((item) => {
    const key = `${item.url || ''}:${item.title || ''}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

function matchesFilters(job, filters) {
  if (filters.client && job.client_name !== filters.client) {
    return false;
  }
  if (filters.matter && job.matter_reference_code !== filters.matter) {
    return false;
  }
  if (filters.status && job.status !== filters.status) {
    return false;
  }
  if (filters.priority && job.priority !== filters.priority) {
    return false;
  }
  return true;
}
